'use client';

import { Icon } from '@/app/components/icons';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { Link } from '@/app/components/navigation/link';
import {
  CV_PRODUCT_EVENT_GROUP,
  CV_PRODUCT_EVENT_NAME,
} from '@/app/cv/components/sections/products/tracking';
import type { ProductCtaSectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

export const ProductCTA = ({ items = [] }: ProductCtaSectionProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });

  if (items.length === 0) return null;

  return (
    <Section
      size="lg"
      className="bg-silver-50"
      data-revealanim-group
      {...createDataAttributes({ test: 'product-cta-section' })}
    >
      <Container
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        data-revealanim
        {...createDataAttributes({ test: 'product-cta-container' })}
      >
        {items.map((item, index) => (
          <Link
            key={index}
            href={item.buttons?.[0]?.href || '#'}
            className="group rounded-base-md relative overflow-hidden"
            {...createDataAttributes({
              test: `cta-${index}`,
            })}
            {...createDataGA({
              eventName: CV_PRODUCT_EVENT_NAME,
              group: CV_PRODUCT_EVENT_GROUP.SERVICE,
              click: true,
              text: item.title,
              url: item.buttons?.[0]?.href || '#',
            })}
          >
            <div className="lg:aspect-3-4 aspect-16-9 relative">
              <Image
                src={item.image?.src || ''}
                alt={item.image?.alt || item.title || ''}
                width={375}
                height={250}
                layout="fill"
                hoverEffect="zoom"
                isGroupHover
                sources={[
                  {
                    srcset: item.imageDesktop?.src || item.image?.src || '',
                    media: {
                      minWidth: 1024,
                    },
                    options: {
                      width: 414,
                      crop: '3:4',
                    },
                  },
                  {
                    srcset: item.image?.src || '',
                    media: {
                      minWidth: 640,
                      maxWidth: 1023,
                    },
                    options: {
                      width: 828,
                      crop: '16:9',
                    },
                  },
                  {
                    srcset: item.image?.src || '',
                    media: {
                      minWidth: 480,
                      maxWidth: 639,
                    },
                    options: {
                      width: 576,
                      crop: '16:9',
                    },
                  },
                  {
                    srcset: item.image?.src || '',
                    media: {
                      maxWidth: 479,
                    },
                    options: {
                      width: 414,
                      crop: '16:9',
                    },
                  },
                ]}
              />
            </div>

            <div
              aria-hidden
              className="absolute inset-0 bg-linear-to-b from-black/0 from-50% to-black/70"
            />

            <div
              className={cn(
                'ease-bounce pointer-events-none absolute bottom-0 flex w-full items-center justify-between gap-x-1 p-4 text-white duration-500',
                'group-hover:-translate-y-2 md:p-3 xl:gap-x-2 xl:p-4'
              )}
            >
              <div>
                {!!item.title && (
                  <h3
                    className="text-h3 font-display"
                    {...createDataAttributes({ test: 'title' })}
                  >
                    {item.title}
                  </h3>
                )}
                {!!item.lead && (
                  <p
                    className="text-body-2"
                    {...createDataAttributes({ test: 'pre-title' })}
                  >
                    {item.lead}
                  </p>
                )}
              </div>
              <Icon
                icon={<IconArrowForward />}
                className="xs:size-6 m-2 size-4"
              />
            </div>
          </Link>
        ))}
      </Container>
    </Section>
  );
};
